import { useEffect, useRef, useState, FormEvent } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, MenuItem, CircularProgress, Typography, Autocomplete } from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';
import useAddContact from '../../../../../hooks/Magasins/useAddContact';
import useUpdateContact from '../../../../../hooks/Magasins/useUpdateContact';
import { useStoreData } from '../../../../../context/StoreDataContext';
import { useOptimisticStore } from '../../../../../context/OptimisticStoreContext';
import { toast } from 'react-toastify';
import { phoneCodes } from '../../../../../utils/phoneCodes';
import { cleanPhoneNumber, formatPhoneNumber, formatPhoneInput } from '../../../../../utils/phoneFormatter';
import { checkPostalCode } from '../../../../../services/postalService';

interface ContactModalProps {
    open: boolean;
    onClose: () => void;
    entityId: string | number;
    contact?: any;
}

const CIVILITY_OPTIONS = [
    { value: '1', label: 'Monsieur' },
    { value: '2', label: 'Madame' },
];

const JOB_OPTIONS = [
    { value: 'DIRECTEUR', label: 'Directeur' },
    { value: 'DIRECTEUR REGIONAL', label: 'Directeur régional' },
    { value: 'MANAGER', label: 'Manager' },
    { value: 'ASSOCIE', label: 'Associé' },
    { value: '-', label: '-' },
];

function ContactModal({ open, onClose, entityId, contact }: ContactModalProps) {
    const { t } = useLanguage();
    const { addContact, isLoading: adding } = useAddContact();
    const { updateContact, isLoading: updating } = useUpdateContact();
    const { countries, wayTypes, loading: isLoadingCountries } = useStoreData();
    const { actualStore, setActualStore } = useOptimisticStore();
    const isEdit = !!contact;
    const isLoading = adding || updating;
    const trapLevelOptions = [
        { value: '0', label: t('pages.store.trapAddress.none') },
        { value: '1', label: t('pages.store.trapAddress.client') },
        { value: '2', label: t('pages.store.trapAddress.kissTheBride') },
    ];
    const [zipCode, setZipCode] = useState(contact?.ZIP_CODE || '');
    const [city, setCity] = useState(contact?.LOCAL_CITY_NAME || '');
    const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
    const [postalLoading, setPostalLoading] = useState(false);
    const zipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const civilityRef = useRef<HTMLInputElement | null>(null);
    const firstNameRef = useRef<HTMLInputElement | null>(null);
    const lastNameRef = useRef<HTMLInputElement | null>(null);
    const jobRef = useRef<HTMLInputElement | null>(null);
    const addressRef = useRef<HTMLInputElement | null>(null);
    const buildingNumberRef = useRef<HTMLInputElement | null>(null);
    const wayTypeRef = useRef<HTMLInputElement | null>(null);
    const wayNameRef = useRef<HTMLInputElement | null>(null);
    const apartmentRef = useRef<HTMLInputElement | null>(null);
    const cityRefRef = useRef<HTMLInputElement | null>(null);
    const placeRef = useRef<HTMLInputElement | null>(null);
    const countryRef = useRef<HTMLInputElement | null>(null);
    const phoneNumberRef = useRef<HTMLInputElement | null>(null);
    const phoneDialingCodeRef = useRef<HTMLInputElement | null>(null);
    const mobileNumberRef = useRef<HTMLInputElement | null>(null);
    const mobileDialingCodeRef = useRef<HTMLInputElement | null>(null);
    const emailRef = useRef<HTMLInputElement | null>(null);
    const trapLevelRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!open) return;

        setZipCode(contact?.ZIP_CODE || '');
        setCity(contact?.LOCAL_CITY_NAME || '');
        setCitySuggestions([]);
    }, [open, contact]);

    useEffect(() => () => {
        if (zipTimer.current) {
            clearTimeout(zipTimer.current);
        }
    }, []);

    const handleZipChange = (value: string) => {
        setZipCode(value);

        if (zipTimer.current) {
            clearTimeout(zipTimer.current);
        }

        if (!/^\d{5}$/.test(value)) {
            setCitySuggestions([]);
            setPostalLoading(false);
            return;
        }

        zipTimer.current = setTimeout(async () => {
            setPostalLoading(true);
            const cities = await checkPostalCode(value);
            setCitySuggestions(cities);
            setPostalLoading(false);
        }, 400);
    };

    const handlePhoneBlur = (ref: React.RefObject<HTMLInputElement | null>) => {
        const input = ref.current;
        if (input) {
            const formatted = formatPhoneInput(input.value);
            input.value = formatted;
        }
    };

    const getFormData = () => ({
        civility: civilityRef.current?.value || '1',
        firstName: firstNameRef.current?.value || '',
        lastName: lastNameRef.current?.value || '',
        job: jobRef.current?.value || 'DIRECTEUR',
        address: addressRef.current?.value || '',
        buildingNumber: buildingNumberRef.current?.value || '',
        wayType: wayTypeRef.current?.value || '',
        wayName: wayNameRef.current?.value || '',
        apartment: apartmentRef.current?.value || '',
        zipCode,
        city,
        cityRef: cityRefRef.current?.value || '',
        place: placeRef.current?.value || '',
        country: countryRef.current?.value || 'FRANCE',
        phoneNumber: phoneNumberRef.current?.value || '',
        phoneDialingCode: phoneDialingCodeRef.current?.value || '+33',
        mobileNumber: mobileNumberRef.current?.value || '',
        mobileDialingCode: mobileDialingCodeRef.current?.value || '+33',
        email: emailRef.current?.value || '',
        trapLevel: trapLevelRef.current?.value || '0',
    });

    const mapFormDataToContact = () => {
        const formData = getFormData();
        return {
            TITLE_ID: formData.civility,
            LASTNAME: formData.lastName,
            FIRSTNAME: formData.firstName,
            JOB: formData.job,
            TRAP_LEVEL: formData.trapLevel,
            ADDRESS1: formData.address,
            ADDRESS2: formData.apartment,
            ADDRESS3_NUM: formData.buildingNumber,
            ADDRESS3_TYPE: formData.wayType,
            ADDRESS3_NAME: formData.wayName,
            ADDRESS4: formData.place,
            ZIP_CODE: formData.zipCode,
            LOCAL_CITY_NAME: formData.city,
            SHORT_CITY_NAME: formData.cityRef,
            LOCAL_COUNTRY_NAME: formData.country,
            PHONE_NUMBER: cleanPhoneNumber(formData.phoneNumber),
            COMM_PHONE_DIALING_CODE: formData.phoneDialingCode,
            CELL_PHONE_NUMBER: cleanPhoneNumber(formData.mobileNumber),
            COMM_CELL_PHONE_DIALING_CODE: formData.mobileDialingCode,
            EMAIL_ADDRESS: formData.email,
        };
    };

    const handleSave = async (e?: FormEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        const formData = getFormData();
        
        if (!formData.firstName || !formData.lastName) {
            toast.error(t('pages.store.errors.contactRequired'));
            return;
        }

        const contactData = {
            title: formData.civility,
            lastname: formData.lastName,
            firstname: formData.firstName,
            job: formData.job,
            trap_level: parseInt(formData.trapLevel),
            address1: formData.address,
            address2: formData.apartment,
            address3_num: formData.buildingNumber,
            address3_type: formData.wayType,
            address3_name: formData.wayName,
            address4: formData.place,
            zip_code: formData.zipCode,
            local_city_name: formData.city,
            short_city_name: formData.cityRef,
            local_country_name: formData.country,
            phone_number: cleanPhoneNumber(formData.phoneNumber),
            comm_phone_dialing_code: formData.phoneDialingCode,
            cell_phone_number: cleanPhoneNumber(formData.mobileNumber),
            comm_cell_phone_dialing_code: formData.mobileDialingCode,
            email_address: formData.email
        };

        if (isEdit) {
            if (actualStore) {
                const updatedContacts = (actualStore.CONTACT_ENTITE || []).map((c: any) =>
                    c.CONTACT_ID === contact.CONTACT_ID ? { ...c, ...mapFormDataToContact() } : c
                );
                setActualStore({ ...actualStore, CONTACT_ENTITE: updatedContacts });
            }

            const result = await updateContact({ contact_id: contact.CONTACT_ID, ...contactData });

            if (result) {
                toast.success(t('pages.store.success.contactUpdated'));
                onClose();
            } else {
                if (actualStore) {
                    const updatedContacts = (actualStore.CONTACT_ENTITE || []).map((c: any) =>
                        c.CONTACT_ID === contact.CONTACT_ID ? contact : c
                    );
                    setActualStore({ ...actualStore, CONTACT_ENTITE: updatedContacts });
                }
                toast.error(t('pages.store.errors.contactUpdateFailed'));
            }
        } else {
            const tempContact = { CONTACT_ID: `temp-${Date.now()}`, ...mapFormDataToContact() };

            if (actualStore) {
                setActualStore({ ...actualStore, CONTACT_ENTITE: [...(actualStore.CONTACT_ENTITE || []), tempContact] });
            }

            const result = await addContact({ entity_id: entityId.toString(), ...contactData });

            if (result) {
                if (actualStore) {
                    // S'assurer que CONTACT_ID est toujours défini
                    const contactId = result.CONTACT_ID || result.id || result.entContact?.id || tempContact.CONTACT_ID;
                    const newContact = {
                        ...tempContact,
                        ...result,
                        CONTACT_ID: contactId
                    };
                    const updatedContacts = [
                        ...(actualStore.CONTACT_ENTITE || []).filter((c: any) => !c.CONTACT_ID.toString().startsWith('temp-')),
                        newContact
                    ];
                    setActualStore({ ...actualStore, CONTACT_ENTITE: updatedContacts });
                }
                toast.success(t('pages.store.success.contactCreated'));
                onClose();
            } else {
                if (actualStore) {
                    const updatedContacts = (actualStore.CONTACT_ENTITE || []).filter((c: any) => c.CONTACT_ID !== tempContact.CONTACT_ID);
                    setActualStore({ ...actualStore, CONTACT_ENTITE: updatedContacts });
                }
                toast.error(t('pages.store.errors.contactCreateFailed'));
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { maxHeight: '90vh' } }}>
            <DialogTitle>
                {isEdit ? (t('pages.store.editContact')) : (t('pages.store.addContact'))}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: '20px!important', overflow: 'auto' }}>
                <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(e); }} id="contact-form">
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    <TextField select fullWidth label={t('pages.store.contactForm.civility')} inputRef={civilityRef} defaultValue={contact?.TITLE_ID?.toString() || '1'} size="small">
                        {CIVILITY_OPTIONS.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                    </TextField>
                    <TextField select fullWidth label={t('pages.store.contactForm.job')} inputRef={jobRef} defaultValue={contact?.JOB || 'DIRECTEUR'} size="small">
                        {JOB_OPTIONS.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                    </TextField>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    <TextField fullWidth required label={t('pages.store.contactForm.lastName')} inputRef={lastNameRef} defaultValue={contact?.LASTNAME || ''} size="small" />
                    <TextField fullWidth required label={t('pages.store.contactForm.firstName')} inputRef={firstNameRef} defaultValue={contact?.FIRSTNAME || ''} size="small" />
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('pages.store.address')}</Typography>
                <TextField fullWidth label={t('pages.store.contactForm.address')} inputRef={addressRef} defaultValue={contact?.ADDRESS1 || ''} sx={{ mb: 2 }} size="small" />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 2fr' }, gap: 2, mb: 2 }}>
                    <TextField fullWidth label={t('pages.store.addressForm.number')} inputRef={buildingNumberRef} defaultValue={contact?.ADDRESS3_NUM || ''} size="small" />
                    <TextField 
                        select 
                        fullWidth 
                        label={t('pages.store.addressForm.wayType')} 
                        inputRef={wayTypeRef}
                        defaultValue={contact?.ADDRESS3_TYPE || ''}
                        size="small"
                        disabled={isLoadingCountries}
                        SelectProps={{
                            MenuProps: {
                                sx: { maxHeight: '300px' },
                            },
                        }}
                    >
                        {isLoadingCountries ? (
                            <MenuItem disabled>{t('common.loading')}...</MenuItem>
                        ) : wayTypes.length === 0 ? (
                            <MenuItem disabled>{t('common.noData')}</MenuItem>
                        ) : (
                            wayTypes.map((wayType) => (
                                <MenuItem key={wayType.id} value={wayType.id.toString()}>
                                    {wayType.name}
                                </MenuItem>
                            ))
                        )}
                    </TextField>
                    <TextField fullWidth label={t('pages.store.addressForm.wayName')} inputRef={wayNameRef} defaultValue={contact?.ADDRESS3_NAME || ''} size="small" />
                </Box>
                <TextField fullWidth label={t('pages.store.addressForm.apartment')} inputRef={apartmentRef} defaultValue={contact?.ADDRESS2 || ''} sx={{ mb: 2 }} size="small" />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
                    <TextField 
                        fullWidth 
                        label={t('pages.store.addressForm.zipCode')} 
                        value={zipCode} 
                        onChange={(e) => handleZipChange(e.target.value)}
                        inputProps={{ maxLength: 5 }}
                        InputProps={{ endAdornment: postalLoading ? <CircularProgress size={14} /> : undefined }}
                        size="small" 
                    />
                    <Autocomplete
                        freeSolo
                        options={citySuggestions}
                        value={city}
                        inputValue={city}
                        onChange={(_, value) => setCity(value || '')}
                        onInputChange={(_, value) => setCity(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                label={t('pages.store.addressForm.city')}
                                size="small"
                                inputProps={{ ...params.inputProps, maxLength: 38 }}
                            />
                        )}
                    />
                    <TextField fullWidth label={t('pages.store.addressForm.cityRef')} inputRef={cityRefRef} defaultValue={contact?.SHORT_CITY_NAME || ''} size="small" />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
                    <TextField fullWidth label={t('pages.store.addressForm.locality')} inputRef={placeRef} defaultValue={contact?.ADDRESS4 || ''} size="small" />
                    <TextField 
                        select 
                        fullWidth 
                        label={t('pages.store.addressForm.country')} 
                        inputRef={countryRef}
                        defaultValue={contact?.LOCAL_COUNTRY_NAME || 'FRANCE'}
                        size="small"
                        disabled={isLoadingCountries}
                        SelectProps={{
                            MenuProps: {
                                sx: { maxHeight: "300px" },
                            },
                        }}
                    >
                        {isLoadingCountries ? (
                            <MenuItem disabled>{t('common.loading')}...</MenuItem>
                        ) : countries.length === 0 ? (
                            <MenuItem disabled>{t('common.noData')}</MenuItem>
                        ) : (
                            countries.map((country) => (
                                <MenuItem key={country.code} value={country.name}>
                                    {country.name}
                                </MenuItem>
                            ))
                        )}
                    </TextField>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
                    <TextField fullWidth label={t('pages.store.contactForm.phoneNumber')} inputRef={phoneNumberRef} defaultValue={formatPhoneNumber(contact?.PHONE_NUMBER) || ''} onBlur={() => handlePhoneBlur(phoneNumberRef)} size="small" />
                    <TextField 
                        select 
                        fullWidth 
                        label={t('pages.store.contactForm.phoneDialingCode')} 
                        inputRef={phoneDialingCodeRef}
                        defaultValue={contact?.COMM_PHONE_DIALING_CODE || '+33'}
                        size="small" 
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            MenuProps: {
                                sx: { height: "300px" },
                            },
                        }}
                    >
                        {phoneCodes.map((item, index) => (
                            <MenuItem key={index} value={item.code}>
                                {item.country} ({item.code})
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
                    <TextField fullWidth label={t('pages.store.contactForm.mobileNumber')} inputRef={mobileNumberRef} defaultValue={formatPhoneNumber(contact?.CELL_PHONE_NUMBER) || ''} onBlur={() => handlePhoneBlur(mobileNumberRef)} size="small" />
                    <TextField 
                        select 
                        fullWidth 
                        label={t('pages.store.contactForm.mobileDialingCode')} 
                        inputRef={mobileDialingCodeRef}
                        defaultValue={contact?.COMM_CELL_PHONE_DIALING_CODE || '+33'}
                        size="small" 
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            MenuProps: {
                                sx: { height: "300px" },
                            },
                        }}
                    >
                        {phoneCodes.map((item, index) => (
                            <MenuItem key={index} value={item.code}>
                                {item.country} ({item.code})
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
                    <TextField fullWidth label={t('pages.store.email')} type="email" inputRef={emailRef} defaultValue={contact?.EMAIL_ADDRESS || ''} size="small" />
                    <TextField select fullWidth label={t('pages.store.contactForm.trapLevel')} inputRef={trapLevelRef} defaultValue={contact?.TRAP_LEVEL || '0'} size="small">
                        {trapLevelOptions.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                    </TextField>
                </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading} color="inherit">{t('common.cancel')}</Button>
                <Button 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSave(e); }} 
                    variant="contained" 
                    disabled={isLoading} 
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    form="contact-form"
                >
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContactModal;
